// Content for SB 1.1.1 second explanation, part 2

import type { TopicContent } from '../types';

const content: TopicContent = {
    primarySloka: {
        reference: 'Srimad Bhagavatam 1.1.1',
        sloka: `oṁ namo bhagavate vāsudevāya
janmādy asya yato 'nvayād itarataś cārtheṣv abhijñaḥ svarāṭ
tene brahma hṛdā ya ādi-kavaye muhyanti yat sūrayaḥ |
tejo-vāri-mṛdāṁ yathā vinimayo yatra tri-sargo 'mṛṣā
dhāmnā svena sadā nirasta-kuhakaṁ satyaṁ paraṁ dhīmahi ||`,
        translation: `Let us meditate upon the Absolute Truth, Kṛṣṇa (<strong>satyaṁ paraṁ dhīmahi</strong>), who is the cause of creation, maintenance and destruction of this universe (<strong>janmādy asya yataḥ</strong>), as the material and efficient cause (<strong>anvayād itarataś ca</strong>), since he is the only knower of all objects (<strong>artheṣv abhijñaḥ</strong>) and the only independent being (<strong>svarāṭ</strong>); who revealed the Vedas to Brahmā within his mind (<strong>tene brahma hṛdā ya ādi-kavaye</strong>); who is difficult to understand even for the learned (<strong>muhyanti yat sūrayaḥ</strong>); whose body is thought to be made of matter due to illusion just as one mistakes fire, water and earth for each other (<strong>tejo-vāri-mṛdāṁ yathā vinimayo yatra tri-sargo ’mṛṣā</strong>); and who negates all false arguments regarding his existence by possessing a transcendental, inconceivable form, and by the strength of devotees’ experiencing him though his svarūpa-śakti (<strong>dhāmnā svena sadā nirasta-kuhakaṁ</strong>).`,
    },
    blocks: [
        {
            type: 'heading',
            headingText: 'Second explanation',
            content:
                'Let us meditate on Kṛṣṇa who is known as Satya, who — through his abode Mathurā and by revelation of his form to his devotees — destroys ignorance concerning himself;<br /><br /><strong>whose body, though appearing to be material to the demons, is not temporary at all;</strong><br /><br />who, after appearing in Vasudeva’s house went to Nanda’s house, knowing how to cheat Kaṁsa and who, knowing the appearance of his devotee’s prema in Vraja, <strong>remained there because of his own people, though he is in all cases independent;</strong><br /><br />and who revealed that the calves and cowherd boys were actually the supreme brahman to Brahmā by his will — which bewilders even persons such as Nārada.',
        },
        {
            type: 'text',
            content: 'It is said in Bhāgavatam:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 2.10.2',
                sloka: `daśamasya viśuddhy-arthaṁ
navānām iha lakṣaṇam
varṇayanti mahātmānaḥ
śrutenārthena cāñjasā`,
                translation:
                    'The great devotees such as Vidura and Maitreya describe properly the nine topics in order to impart the highest knowledge of the tenth topic, through the words of the scripture and stories to illustrate their meaning.',
            },
        },
        {
            type: 'text',
            content:
                'Since the Supreme Lord is the aṅgī in relation to all other topics, and since Kṛṣṇa is the chief form of the Lord, and since there is praise for his extraordinary qualities, Kṛṣṇa should be indicated in the first verse.<br /><br />Thus a second explanation of the first verse has arisen devoted only to Kṛṣṇa. This is indicated at the beginning with the description of Kṛṣṇa’s birth. <strong>(Satyam param dhīmahi is explained as Kṛṣṇa.)</strong>',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 10.2.26',
                sloka: `satya-vrataṁ satya-paraṁ tri-satyaṁ
satyasya yoniṁ nihitaṁ ca satye
satyasya satyam ṛta-satya-netraṁ
satyātmakaṁ tvāṁ śaraṇaṁ prapannāḥ`,
                translation:
                    'O Lord, You never deviate from your vow, which is always perfect because whatever Yu decide is perfectly correct and cannot be stopped by anyone. Being present in the three phases of cosmic manifestation —creation, maintenance and annihilation — you are the Supreme Truth. Indeed, unless one is completely truthful, one cannot achieve your favor, which therefore cannot be achieved by hypocrites. You are the active principle, the real truth, in all the ingredients of creation, and therefore you are known as antaryāmī, the inner force. You are equal to everyone, and your instructions apply for everyone, for all time. You are the beginning of all truth. Therefore, offering our obeisances, we surrender unto you. Kindly give us protection.',
            },
        },
        {
            type: 'text',
            content: 'Sañjaya, explaining the derivation of Kṛṣṇa’s names, explains why Kṛṣṇa is addressed as Satya:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Mahābhārata 5.68.12',
                sloka: `satye pratiṣṭhitaḥ kṛṣṇaḥ
satyam atra pratiṣṭhitam |
satyāt satyaṁ ca govindas
tasmāt satyo hi nāmataḥ ||`,
                translation:
                    'Kṛṣṇa is fixed in the highest truth, and truth is fixed in him. Because all truth arises from him, Govinda is respected as truth or satya.',
            },
        },
        {
            type: 'text',
            content:
                'Brahmāṇḍa Purāṇa also says satyaṁ śrī-kṛṣnaṁ dhīmahi paraṁ brahma narākṛtī: we meditate upon Kṛṣṇa, the supreme brahman with human form. And Gopāla-tāpanī Upaniṣad says tasmāt kṛṣṇa eva paro devas taṁ dhyāyet: therefore Kṛṣṇa is the supreme Lord, and one should meditate on him.',
        },
        {
            type: 'heading',
            headingText: 'Mathurā and the Self-Revealed Form',
            content:
                'Let us meditate on Kṛṣṇa, the jīvas’ ignorance of whom (kuhakam) is always destroyed by his abode called Mathurā and by his form (svena dhāmnā) which can be seen by mercy even today. The two causes are illustrated by the following two verses.',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Mathurā definition verse',
                sloka: `mathyate tu jagat sarvaṁ
brahma-jñānena yena vā |
tat-sāra-bhūtaṁ yad yasyāṁ
mathurā sā nigadyate ||`,
                translation:
                    'Mathurā is defined as that place which excites the whole world as it is non-different from the Lord, or that place in which the essence of spiritual knowledge is extracted (mathyate).',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 10.70.43',
                sloka: `śravaṇāt kīrtanād dhyānāt
pūyante ’nte-vasāyinaḥ
tava brahma-mayasyeśa
kim utekṣābhimarśinaḥ`,
                translation:
                    'O Lord, even outcastes are purified by hearing and chanting your glories and meditating upon you, the Absolute Truth. What then to speak of those who see and touch you?',
            },
        },
        {
            type: 'text',
            content:
                'According to Amara-koṣa, dhāma means house, body, effulgence and influence.',
        },
        {
            type: 'dialogue',
            objection:
                '“Whatever is seen by the material eye is temporary. Because Kṛṣṇa’s form can be seen in the material world it must be temporary.”',
            response:
                'The bodies made of the three guṇas (tri-sargaḥ) by the combination of three visible elements earth, water and air, are false (temporary). But the form which created those three guṇas is not false (not temporary).<br /><br /><strong>The transcendental body of the Lord, though seen by the demons with material bodies, is seen by them without realization of its sweetness, just as sugar candy has no sweet taste when eaten by a person afflicted with jaundice.</strong>',
        },
        {
            type: 'text',
            content:
                'This is because of the will of the Lord, arising from him inconceivable svarūpa, which accomplished his various pastimes. The devotees, however, can realize that form of sweetness by the influence of the Lord’s inconceivable mercy.',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 10.86.20-21',
                sloka: `ānarta-dhanva-kuru-jāṅgala-kaṅka-matsya-
pāñcāla-kunti-madhu-kekaya-kośalārṇāḥ
anye ca tan-mukha-sarojam udāra-hāsa-
snigdhekṣaṇaṁ nṛpa papur dṛśibhir nr-nāryaḥ
tebhyaḥ sva-vīkṣaṇa-vinaṣṭa-tamisra-dṛgbhyaḥ
kṣemaṁ tri-loka-gurur artha-dṛśaṁ ca yacchan
śṛṇvan dig-anta-dhavalaṁ sva-yaśo ’śubha-ghnaṁ
gītaṁ surair nṛbhir agāc chanakair videhān`,
                translation:
                    'The men and women of Ānarta, Dhanva, Kuru-jāṅgala, Kaṅka, Matsya, Pañcāla, Kunti, Madhu, Kekaya, Kośala, Arṇa and many other kingdoms drank with their eyes the nectarean beauty of Lord Kṛṣṇa’s lotuslike face, which was graced with generous smiles and affectionate glances. Simply by glancing at those who came to see him, Lord Kṛṣṇa, the spiritual master of the three worlds, delivered them from the blindness of materialism. As he thus endowed them with fearlessness and divine vision, he heard demigods and men singing his glories, which purify the entire universe and destroy all misfortune. Gradually, he reached Videha.',
            },
        },
        {
            type: 'text',
            content: 'These verses show that by his mercy alone his great powers can be understood.',
        },
        {
            type: 'text',
            content: 'Nārāyaṇādhyātma says:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Nārāyaṇādhyātma',
                sloka: `nityāvyakto ’pi bhagavān
īkṣate nija-śaktitaḥ
tām ṛte paramānandaṁ
kaḥ paśyetām itaṁ prabhum`,
                translation:
                    'Though the lord is eternally invisible, he is seen by his own energy. Who can see the Lord full of supreme bliss without that energy?',
            },
        },
        {
            type: 'text',
            content: 'Here is an explanatory verse:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Explanatory verse',
                sloka: `tataḥ svayaṁ prakāśatva-
śaktyā svecchā-prakāśayā
so ’bhivyakto bhaven netre
na netra-viṣayaḥ kṛtaḥ`,
                translation:
                    'Thus by his self-manifesting energy, arising by his own will, the Lord, who is not visible to the eye, becomes visible to the eyes.',
            },
        },
        {
            type: 'text',
            content:
                'The śruti says tāsāṁ madhye sākṣāt brahma gopāla-purī hi: in the middle of that expanse one can see the abode of Kṛṣṇa made of brahman. (Gopāla-tāpanī Upaniṣad)<br /><br /><strong>Thus the abodes of the Lord, also made of spiritual matter become visible.</strong> Those who are knowledgeable by the Lord’s mercy conclude that things like pots, which are seen and which are not spiritual, are temporary.',
        },
        {
            type: 'heading',
            headingText: 'From Vāsudeva’s House to Nanda’s House',
            content:
                'Having explained that the cause of the Lord’s appearance is his mercy, his pastimes are described. I meditate upon Kṛṣṇa, who after his <strong>birth (<em>janmādi yataḥ</em>) in Vāsudeva’s house</strong> showed the form of Viṣṇu, described his previous appearances, etc. and <strong>later went (<em>anu ayāt</em>) elsewhere (<em>itarataḥ</em>), to the house of Nanda.</strong><br /><br />Why did he go? Knowing his own purposes <strong>(<em>abhijñaḥ artheṣu</em>)</strong>, such as, how to cheat Kaṁsa or knowing those who had revealed forms with various types of prema such as vātsalaya in Vraja, he went to Nanda’s house. But this does not mean that he is dependent on others. <strong>He is independent, ruling over himself (<em>svarāṭ=svena rājate</em>).</strong> Or <em>svarāṭ</em> can mean that he remained there in Vraja (<em>virājate</em>) because of his relatives such as Nanda (<em>svaiḥ</em>).',
        },
        {
            type: 'text',
            content:
                'Because of his performing such pastimes under the control of prema of his devotees in Vraja, one should not think that he is a fool. <strong>He revealed (<em>tene</em>) the calves and cowherd boys to be brahman (<em>brahma</em>) even unto Lord Brahmā (<em>ādi-kavaye</em>), by his will alone (<em>hṛdā</em>)</strong>, by which, through his power of yoga-māyā, even the intelligence of persons like Śiva and Nārada are bewildered.',
        },
        {
            type: 'text',
            content:
                'Or Kṛṣṇa by the statements of his svāṁśa form Matsya revealed his form as impersonal brahman to Satyavrata (ādi-kavaye), who was the wise man (kavi) and founder of his dynasty (ādi). This is described in the following verse:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 8.24.38',
                sloka: `madīyaṁ mahimānaṁ ca
paraṁ brahmeti śabditam |
vetsyasy anugṛhītaṁ me
sampraśnair vivṛtaṁ hṛdi ||`,
                translation:
                    'By my mercy, you will realize my power known as the impersonal Brahman, which will be disclosed in your heart through questions and answers.',
            },
        },
        {
            type: 'text',
            content:
                'Śrīdhara Svāmī explains this verse as follows: you will directly see (vetsyasi) this brahman, which will be by my mercy (me ahugṛhītam); it will be explained at the beginning of the prayers of the Personified Vedas (SB10.87.2) that the brahman is realized by the Lord’s mercy alone.',
        },
        {
            type: 'text',
            content:
                '<strong>Thus, in this second explanation, satyaṁ paraṁ dhīmahi culminates in direct meditation on Kṛṣṇa as Svayaṁ Bhagavān—the Supreme Truth who reveals Himself by His own mercy.</strong>',
        },
    ],
};

export default content;
